// A small line diff for the override "Diff vs global" view. Zero imports: node-tested. LCS on lines;
// fine for method bodies (hundreds of lines), not meant for anything large.

export type DiffOp = { type: 'same' | 'add' | 'remove'; text: string };

export function diffLines(before: string, after: string): DiffOp[] {
  const a = before.split('\n');
  const b = after.split('\n');
  const n = a.length;
  const m = b.length;
  // lcs[i][j] = length of LCS of a[i:], b[j:]
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i]![j] = a[i] === b[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }
  const ops: DiffOp[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      ops.push({ type: 'same', text: a[i]! });
      i++;
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      ops.push({ type: 'remove', text: a[i]! });
      i++;
    } else {
      ops.push({ type: 'add', text: b[j]! });
      j++;
    }
  }
  while (i < n) ops.push({ type: 'remove', text: a[i++]! });
  while (j < m) ops.push({ type: 'add', text: b[j++]! });
  return ops;
}

export function diffStats(ops: readonly DiffOp[]): { added: number; removed: number } {
  return {
    added: ops.filter((o) => o.type === 'add').length,
    removed: ops.filter((o) => o.type === 'remove').length,
  };
}

/** Strip YAML frontmatter so a diff compares method bodies, not headers. */
export function stripFrontmatter(text: string): string {
  return text.replace(/^---\n[\s\S]*?\n---\n/, '').trim();
}

/** How an override's expiry reads to an operator. */
export function expiryLabel(expiresAt: number | undefined, now: number): { text: string; tone: 'ok' | 'soon' | 'review' } {
  if (expiresAt === undefined) return { text: 'No expiry set — needs review', tone: 'review' };
  const days = Math.ceil((expiresAt - now) / 86_400_000);
  if (days <= 0) return { text: 'Expired', tone: 'review' };
  if (days <= 7) return { text: `Expires in ${days} day${days === 1 ? '' : 's'}`, tone: 'soon' };
  return { text: `Expires in ${days} days`, tone: 'ok' };
}
